import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaLock,
  FaSignInAlt,
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaUserShield,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import api from "../../services/api";
import { z } from "zod";

// Zod schema for validation
const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [buttonState, setButtonState] = useState("idle"); // idle, success, failed
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { email, password } = formData;

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const clearError = (field) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setButtonState("idle");
    setErrors({ email: "", password: "" });

    // Zod validation
    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors = result.error.format();
      setErrors({
        email: fieldErrors.email?._errors[0] || "",
        password: fieldErrors.password?._errors[0] || "",
      });
      setLoading(false);
      return;
    }

    try {
      const res = await api.post("/admin/login", { email, password });

      if (res.data.user.role !== "admin") {
        setErrors({
          email: "",
          password: "Access Denied: Admin privileges required.",
        });
        setButtonState("failed");
        setTimeout(() => setButtonState("idle"), 2000);
        setLoading(false);
        return;
      }

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));

      // Success state
      setButtonState("success");
      setTimeout(() => {
        navigate("/admin");
      }, 1500);
    } catch (err) {
      console.error(err);
      const errorMsg =
        err.response?.data?.message ||
        "Authentication failed. Please check your credentials.";
      setErrors({
        email: "",
        password: errorMsg,
      });
      setButtonState("failed");
      setTimeout(() => setButtonState("idle"), 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full">
        {/* Back to Main Site */}
        <div className="mb-6">
          <a
            href="/"
            className="inline-flex items-center text-sm text-gray-400 hover:text-gray-300 transition-colors duration-200 group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            Back to Main Site
          </a>
        </div>

        {/* Main Card */}
        <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900 to-gray-900 p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-16 -translate-y-16"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/5 rounded-full translate-x-20 translate-y-20"></div>

            <div className="relative z-10">
              <div className="mx-auto w-20 h-20 bg-blue-500/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5 border border-blue-500/20">
                <FaUserShield className="text-3xl text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Admin Portal
              </h1>
              <p className="text-gray-400 text-sm">
                Secure Access Verification
              </p>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={onSubmit} className="p-8 space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-400">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="text-gray-500 group-hover:text-blue-400 transition-colors duration-200" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    onChange(e);
                    clearError("email");
                  }}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-xl bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-sm ${
                    errors.email
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-600 focus:border-blue-500"
                  }`}
                  placeholder="admin@example.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-400 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-400">
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="text-gray-500 group-hover:text-blue-400 transition-colors duration-200" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(e) => {
                    onChange(e);
                    clearError("password");
                  }}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-xl bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-sm ${
                    errors.password
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-600 focus:border-blue-500"
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-blue-400 transition-colors duration-200"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-400 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            {/* Login Button with Animation States */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || buttonState !== "idle"}
                className={`w-[280px] flex justify-center items-center py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-500 transform ${
                  buttonState === "success"
                    ? "bg-green-600 scale-110"
                    : buttonState === "failed"
                    ? "bg-red-600 scale-110"
                    : loading
                    ? "bg-blue-800 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 border ${
                  buttonState === "success"
                    ? "border-green-500"
                    : buttonState === "failed"
                    ? "border-red-500"
                    : "border-blue-700"
                }`}
              >
                {buttonState === "success" ? (
                  <>
                    <FaCheck className="mr-2 text-lg animate-ping-once" />
                    Login Successful
                  </>
                ) : buttonState === "failed" ? (
                  <>
                    <FaTimes className="mr-2 text-lg animate-pulse" />
                    Login Failed
                  </>
                ) : loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <FaSignInAlt className="mr-2 text-sm" />
                    Login to Dashboard
                  </>
                )}
              </button>
            </div>

            {/* Security Note */}
            <div className="pt-4 border-t border-gray-700">
              <div className="flex items-center justify-center text-gray-500">
                <FaUserShield className="mr-2 text-xs" />
                <span className="text-xs">Secure administrative portal</span>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 text-sm">
            © {new Date().getFullYear()} Admin Portal. All rights reserved.
          </p>
        </div>
      </div>

      {/* Custom Animation Keyframes */}
      <style jsx>{`
        @keyframes ping-once {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.4);
          }
          100% {
            transform: scale(1);
          }
        }
        .animate-ping-once {
          animation: ping-once 0.8s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;